import { PIECES_BY_VALUE } from '../consts/pieces.js';
import { decode62, encode62, encode62Fixed } from './base62.js';
import { decodeLegacyPosition } from './legacy.js';

export const POSITION_PREFIX = 'P1|';

const GRAVEYARD_VALUES = [1, 2, 3, 4, 5, 6, 7];
const CELL_WIDTH = 2;

function flattenBoard(board = []) {
  if (!Array.isArray(board) || board.length === 0) return [];
  if (Array.isArray(board[0])) return board.flatMap((row) => row);
  return board;
}

function toBoardGrid(cells) {
  return Array(4)
    .fill()
    .map((_, y) =>
      Array(8)
        .fill()
        .map((_, x) => cells[y * 8 + x])
    );
}

/** Pack id(0-31) × teamMapped(0-2) × value(0-8) into 0..863 */
export function packCell({ id = 0, teamIndex = -1, value = 0 } = {}) {
  const teamMapped = Number(teamIndex) + 1;
  return Number(id) * 27 + teamMapped * 9 + Number(value);
}

export function unpackCell(packed) {
  const value = packed % 9;
  const rest = Math.floor(packed / 9);
  const teamMapped = rest % 3;
  const id = Math.floor(rest / 3);
  return { id, teamIndex: teamMapped - 1, value };
}

function encodeBoard(board) {
  return flattenBoard(board)
    .map((cell) => encode62Fixed(packCell(cell), CELL_WIDTH))
    .join('');
}

function decodeBoard(encodedBoard) {
  if (!encodedBoard || encodedBoard.length !== 32 * CELL_WIDTH) {
    throw new Error('Invalid P1 board length');
  }
  const cells = [];
  for (let i = 0; i < 32; i++) {
    const token = encodedBoard.slice(i * CELL_WIDTH, (i + 1) * CELL_WIDTH);
    cells.push(unpackCell(decode62(token)));
  }
  return toBoardGrid(cells);
}

function encodeGraveyard(graveyard = []) {
  return [0, 1]
    .map((playerIndex) => {
      const counts = {};
      for (const piece of graveyard) {
        if (piece.teamIndex !== playerIndex) continue;
        counts[piece.value] = (counts[piece.value] || 0) + 1;
      }
      return GRAVEYARD_VALUES.map((v) => String(counts[v] || 0)).join('');
    })
    .join('');
}

function decodeGraveyard(encodedGraveyard) {
  if (!encodedGraveyard || encodedGraveyard.length !== 14) {
    throw new Error('Invalid P1 graveyard');
  }
  const pieces = [];
  for (let playerIndex = 0; playerIndex < 2; playerIndex++) {
    const chunk = encodedGraveyard.slice(playerIndex * 7, playerIndex * 7 + 7);
    GRAVEYARD_VALUES.forEach((value, i) => {
      const count = Number(chunk[i] || 0);
      for (let n = 0; n < count; n++) {
        pieces.push({
          ...PIECES_BY_VALUE[value],
          teamIndex: playerIndex,
        });
      }
    });
  }
  return pieces;
}

function encodeFollowing(following = {}) {
  return [0, 1]
    .map((playerIndex) => {
      const playerFollowing = following[playerIndex];
      if (!playerFollowing || playerFollowing.pieceId == null) return '-';
      const { pieceId, following: followMap = {} } = playerFollowing;
      const pairs = Object.entries(followMap)
        .map(
          ([otherPieceId, turns]) =>
            `${encode62(+otherPieceId)}.${encode62(+turns)}`
        )
        .join(',');
      return `${encode62(pieceId)}:${pairs}`;
    })
    .join('/');
}

function decodeFollowing(encodedFollowing) {
  const parts = String(encodedFollowing).split('/');
  return parts.reduce((all, encoded, playerIndex) => {
    if (!encoded || encoded === '-') return all;
    const colon = encoded.indexOf(':');
    if (colon < 0) return all;
    const pieceId = decode62(encoded.slice(0, colon));
    const pairsPart = encoded.slice(colon + 1);
    const following = {};
    if (pairsPart) {
      for (const pair of pairsPart.split(',')) {
        if (!pair) continue;
        const [otherId, turns] = pair.split('.');
        following[decode62(otherId)] = decode62(turns);
      }
    }
    return {
      ...all,
      [playerIndex]: { pieceId, following },
    };
  }, {});
}

export function encodePosition({
  turnNumber = 0,
  board = [],
  graveyard = [],
  following = {},
  turnsWithoutAction = 0,
  lastActionPlayerIndex = 0,
} = {}) {
  return [
    'P1',
    encode62(turnNumber),
    encodeBoard(board),
    encodeGraveyard(graveyard),
    encodeFollowing(following),
    encode62(turnsWithoutAction ?? 0),
    encode62(lastActionPlayerIndex ?? 0),
  ].join('|');
}

export function decodePositionP1(encodedGameState) {
  const parts = String(encodedGameState).split('|');
  if (parts[0] !== 'P1' || parts.length < 7) {
    throw new Error('Invalid P1 position');
  }
  const [
    ,
    encodedTurnNumber,
    encodedBoard,
    encodedGraveyard,
    encodedFollowing,
    encodedTurnsWithoutAction,
    encodedLastActionPlayerIndex,
  ] = parts;

  return {
    turnNumber: decode62(encodedTurnNumber),
    board: decodeBoard(encodedBoard),
    graveyard: decodeGraveyard(encodedGraveyard),
    following: decodeFollowing(encodedFollowing),
    turnsWithoutAction: decode62(encodedTurnsWithoutAction),
    lastActionPlayerIndex: decode62(encodedLastActionPlayerIndex),
  };
}

export function decodePosition(encodedGameState) {
  const raw = String(encodedGameState ?? '');
  if (raw.startsWith('P1|')) return decodePositionP1(raw);
  return decodeLegacyPosition(raw);
}

export function isP1Position(encodedGameState) {
  return String(encodedGameState ?? '').startsWith('P1|');
}
